import { useEffect, useRef, useState } from 'react';
import { matchesQuery } from '../lib/search';
import { useT } from '../lib/i18n';

export interface ComboOption {
  value: string;
  label: string;
}

/**
 * A searchable single-select dropdown that replaces the native `<select>` for
 * fields with long or numerous values. Unlike a native select it can show the
 * full value (wrapping) and lets the user type to filter — solving the
 * "long content is cut" problem on the ficha. Keyboard: ↑/↓ to move, Enter to
 * pick, Esc to close. When disabled it renders the value as plain text.
 */
export function Combobox({
  value,
  options,
  onChange,
  disabled,
  placeholder = '—',
}: {
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? value ?? '';
  // Same "smart" matching as the Colaboradores list: tokens in any order,
  // accent-insensitive (e.g. "costa maria" finds "Maria Alberta Costa").
  const q = query.trim();
  const filtered = q ? options.filter((o) => matchesQuery(o.label, q)) : options;
  // Index 0 is the "clear" (—) row; options follow at 1..n.
  const rows = filtered.length + 1;

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  function choose(v: string) {
    onChange(v);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active === 0) choose('');
      else choose(filtered[active - 1]?.value ?? value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  if (disabled) {
    return (
      <div className={'combo-static' + (label ? '' : ' is-empty')} title={label || undefined}>
        {label || placeholder}
      </div>
    );
  }

  return (
    <div className="combo" ref={rootRef}>
      <button
        type="button"
        className="combo-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label || undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={'combo-value' + (label ? '' : ' is-empty')}>{label || placeholder}</span>
        <svg
          className="combo-caret"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="combo-pop">
          <div className="combo-search">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              placeholder={`${t('common.search')}…`}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
            />
          </div>
          <ul className="combo-list" role="listbox">
            <li
              role="option"
              aria-selected={!value}
              className={
                'combo-opt' + (active === 0 ? ' active' : '') + (!value ? ' selected' : '')
              }
              onMouseEnter={() => setActive(0)}
              onClick={() => choose('')}
            >
              <span className="combo-opt-label is-empty">{placeholder}</span>
            </li>
            {filtered.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={
                  'combo-opt' +
                  (active === i + 1 ? ' active' : '') +
                  (o.value === value ? ' selected' : '')
                }
                onMouseEnter={() => setActive(i + 1)}
                onClick={() => choose(o.value)}
              >
                <span className="combo-opt-label">{o.label}</span>
              </li>
            ))}
            {filtered.length === 0 && <li className="combo-empty">Sem resultados</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
